import React, { useCallback, useEffect } from 'react'
import { QueryClient } from '@tanstack/react-query'
import { Params } from 'react-router-dom'
import {
  couponDetailInfoTabQueryOption,
  couponDetailQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import { CouponDetailInfoTabSpace, CouponDetailResponseType } from 'src/types/api'
import useParamsType, { MatchParams } from 'src/hooks/useParamsType.hook'
import { Loading } from 'src/components/common/Loading'
import usePageNav from 'src/hooks/usePageNav.hook'
import RenderIf from 'src/components/common/RenderIf'
import { mobileOS } from 'src/utils'
import GiftHotDealDetailPageView from 'src/pages/GiftHotDealPage/GiftHotDealDetailPage/view'
import { aosOpenApp, iosIntroDeepLink } from 'src/utils/windowOpen'

export interface GiftHotDealDetailPageProps {
  couponDetailData?: CouponDetailResponseType
  couponDetailTabData?: CouponDetailInfoTabSpace.Response
  openService: () => void
}

export const giftHotDealDetailPageLoader =
  (queryClient: QueryClient) =>
  async ({ params: { couponIdx, couponNumber } }: { params: Params<keyof MatchParams> }) => {
    return Promise.allSettled([
      await queryClient.ensureQueryData(
        couponDetailQueryOption('1', { CouponIdx: Number(couponIdx!) }),
      ),
      await queryClient.ensureQueryData(
        couponDetailInfoTabQueryOption({ CouponType: 1, CouponNumber: couponNumber! }),
      ),
    ])
  }

const GiftHotDealDetailPage = () => {
  const { couponIdx, couponNumber } = useParamsType()
  const { splashPageNav } = usePageNav()
  const os = mobileOS()

  const { data: couponDetailQueryData, isLoading: isCouponDetailLoading } =
    useCustomQuery<CouponDetailResponseType>(
      couponDetailQueryOption('1', { CouponIdx: Number(couponIdx) }),
    )

  const {
    data: couponDetailInfoTabQueryData,
    isLoading: isCouponDetailInfoLoading,
    isSuccess: isCouponDetailInfoSuccess,
  } = useCustomQuery<CouponDetailInfoTabSpace.Response>(
    couponDetailInfoTabQueryOption({ CouponType: 1, CouponNumber: couponNumber }),
  )

  const setCouponDetailInfoItems = useCallback(() => {
    if (!!couponDetailInfoTabQueryData?.Body) {
      const couponDetailInfoTabData = couponDetailInfoTabQueryData?.Body!
      const { Title, Contents } = couponDetailInfoTabData.CouponInfo!

      return {
        ...couponDetailInfoTabData,
        CouponInfo: {
          Title: Title,
          Contents: Contents?.replace('{{ValidDay}}', `${couponDetailQueryData?.Body?.ValidDay}일`),
        },
      }
    } else return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCouponDetailInfoSuccess, couponDetailInfoTabQueryData])

  const openService = () => {
    const webUrl = `${process.env.REACT_APP_SERVICE_URL}/web/splash`
    switch (os) {
      case 'android':
        window.location.href = aosOpenApp(webUrl)
        break
      case 'ios':
        window.location.href = iosIntroDeepLink
        break
      default:
        splashPageNav()
        break
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  const props: GiftHotDealDetailPageProps = {
    couponDetailData: couponDetailQueryData?.Body,
    couponDetailTabData: setCouponDetailInfoItems(),
    openService,
  }

  return (
    <React.Fragment>
      <RenderIf when={isCouponDetailLoading || isCouponDetailInfoLoading}>
        <Loading.Default />
      </RenderIf>
      <GiftHotDealDetailPageView {...props} />
    </React.Fragment>
  )
}
export default GiftHotDealDetailPage
